// InvoiceExport.js
import { useState, useEffect } from 'react';
import { Search, X, Filter, CheckSquare, Square, Download, User, DollarSign, CheckCircle, XCircle, Clock, FileText, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useTranslation } from '../../i18n/i18n';
import { toast } from 'react-toastify';

// Import jsPDF
import jsPDF from 'jspdf';

// CORRECTION : Importer autoTable correctement
import autoTable from 'jspdf-autotable';

const InvoiceExport = ({ 
  isOpen, 
  onClose, 
  invoice, 
  formatDate,
  getServiceIcon,
  lang
}) => {
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');
  const { t } = useTranslation(lang);
  
  // Initialiser les sélections quand l'invoice est chargé
  useEffect(() => {
    if (invoice && invoice.bookings) {
      // Sélectionner toutes les réservations par défaut
      setSelectedBookings(invoice.bookings.map(b => b.booking_id));
    }
  }, [invoice]);

  // Toggle la sélection d'une réservation
  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  // Toggle toutes les réservations
  const toggleAllBookings = () => {
    if (!invoice?.bookings) return;
    
    if (selectedBookings.length === invoice.bookings.length) {
      // Tout désélectionner
      setSelectedBookings([]);
    } else {
      // Tout sélectionner
      setSelectedBookings(invoice.bookings.map(b => b.booking_id));
    }
  };

  // Filtrer les réservations selon les critères
  const getFilteredBookings = () => {
    if (!invoice?.bookings) return [];
    
    return invoice.bookings.filter(booking => {
      // Filtre par recherche
      const matchesSearch = searchTerm === '' || 
        booking.booking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.holder.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.service_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre par statut
      const matchesStatus = filterStatus === '' || 
        booking.status.toLowerCase() === filterStatus.toLowerCase();
      
      // Filtre par service
      const matchesService = filterService === '' || 
        booking.service.toLowerCase() === filterService.toLowerCase();
      
      return matchesSearch && matchesStatus && matchesService;
    });
  };

  // Calculer les totaux par currency
  const calculateTotalsByCurrency = (bookings) => {
    const totals = {};
    
    bookings.forEach(booking => {
      const currency = booking.currency;
      const amount = parseFloat(booking.amount);
      
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      
      totals[currency] += amount;
    });
    
    return totals;
  };

  // Fonction d'export Excel (existante)
  const exportToExcel = () => {
    if (!invoice) return;
    
    try {
      // Récupérer les réservations sélectionnées
      const selectedBookingData = invoice.bookings.filter(b => 
        selectedBookings.includes(b.booking_id)
      );
      
      if (selectedBookingData.length === 0) {
        toast.error(t('noBookingsSelected'));
        return;
      }
      
      // Calculer les totaux par currency
      const totalsByCurrency = calculateTotalsByCurrency(selectedBookingData);
      
      // 1. Créer la feuille pour les informations de la facture
      const invoiceData = [
        // Section: Informations de la facture
        { [t('section')]: t('invoiceInformation'), [t('title')]: t('invoiceNumber'), [t('value')]: invoice.invoice_number },
        { [t('section')]: t('invoiceInformation'), [t('title')]: t('invoiceDate'), [t('value')]: formatDate(invoice.invoice_at) },
        { [t('section')]: t('invoiceInformation'), [t('title')]: t('dueDate'), [t('value')]: formatDate(invoice.due_at) },
        { [t('section')]: t('invoiceInformation'), [t('title')]: t('status'), [t('value')]: invoice.status },
        { [t('section')]: t('invoiceInformation'), [t('title')]: t('amount'), [t('value')]: `${parseFloat(invoice.amount).toFixed(2)} ${invoice.currency}` },
        { [t('section')]: t('invoiceInformation'), [t('title')]: t('paidDate'), [t('value')]: invoice.paid_at ? formatDate(invoice.paid_at) : t('notPaid') },
        
        // Ligne vide pour séparer
        { [t('section')]: '', [t('title')]: '', [t('value')]: '' },
        
        // Section: Émetteur
        { [t('section')]: t('issuer'), [t('title')]: t('name'), [t('value')]: invoice.issuer?.name || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('company'), [t('value')]: invoice.issuer?.company || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('address'), [t('value')]: invoice.issuer?.address || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('city'), [t('value')]: invoice.issuer?.city || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('zipCode'), [t('value')]: invoice.issuer?.zipCode || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('country'), [t('value')]: invoice.issuer?.country || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('email'), [t('value')]: invoice.issuer?.email || '' },
        { [t('section')]: t('issuer'), [t('title')]: t('phone'), [t('value')]: invoice.issuer?.phone || '' },
        
        // Ligne vide pour séparer
        { [t('section')]: '', [t('title')]: '', [t('value')]: '' },
        
        // Section: Client
        { [t('section')]: t('customer'), [t('title')]: t('name'), [t('value')]: invoice.customer?.name || '' },
        { [t('section')]: t('customer'), [t('title')]: t('company'), [t('value')]: invoice.customer?.company || '' },
        { [t('section')]: t('customer'), [t('title')]: t('address'), [t('value')]: invoice.customer?.address || '' },
        { [t('section')]: t('customer'), [t('title')]: t('city'), [t('value')]: invoice.customer?.city || '' },
        { [t('section')]: t('customer'), [t('title')]: t('zipCode'), [t('value')]: invoice.customer?.zipCode || '' },
        { [t('section')]: t('customer'), [t('title')]: t('country'), [t('value')]: invoice.customer?.country || '' },
        { [t('section')]: t('customer'), [t('title')]: t('email'), [t('value')]: invoice.customer?.email || '' },
        { [t('section')]: t('customer'), [t('title')]: t('phone'), [t('value')]: invoice.customer?.phone || '' },
        
        // Ligne vide pour séparer
        { [t('section')]: '', [t('title')]: '', [t('value')]: '' },
        
        // Section: Informations d'export
        { [t('section')]: t('exportInformation'), [t('title')]: t('exportDate'), [t('value')]: new Date().toLocaleString(lang) },
        { [t('section')]: t('exportInformation'), [t('title')]: t('totalBookings'), [t('value')]: invoice.bookings.length },
        { [t('section')]: t('exportInformation'), [t('title')]: t('selectedBookings'), [t('value')]: selectedBookings.length },
        { [t('section')]: t('exportInformation'), [t('title')]: t('totalAmount'), [t('value')]: `${parseFloat(invoice.amount).toFixed(2)} ${invoice.currency}` }
      ];
      
      const wsInvoice = XLSX.utils.json_to_sheet(invoiceData);
      
      // 2. Créer la feuille pour les réservations
      const bookingsData = selectedBookingData.map(booking => ({
        [t('bookingId')]: booking.booking_id,
        [t('rateId')]: booking.rate_id,
        [t('bookingNumber')]: booking.booking_number,
        [t('service')]: booking.service,
        [t('serviceName')]: booking.service_name,
        [t('productName')]: booking.product_name,
        [t('holder')]: booking.holder,
        [t('checkin')]: formatDate(booking.checkin),
        [t('checkout')]: formatDate(booking.checkout),
        [t('status')]: booking.status,
        [t('amount')]: parseFloat(booking.amount).toFixed(2),
        [t('currency')]: booking.currency,
        [t('createdAt')]: new Date(booking.created_at).toLocaleString(lang),
      }));
      
      const wsBookings = XLSX.utils.json_to_sheet(bookingsData);
      
      // 3. Créer le classeur Excel
      const wb = XLSX.utils.book_new();
      
      // Ajouter la feuille des informations de facture
      XLSX.utils.book_append_sheet(wb, wsInvoice, t('invoiceInfo'));
      
      // Ajouter la feuille des réservations
      XLSX.utils.book_append_sheet(wb, wsBookings, t('bookings'));
      
      // Formatage pour la feuille invoice
      if (wsInvoice['!ref']) {
        const wscolsInvoice = [
          { wch: 25 }, // Colonne Section
          { wch: 25 }, // Colonne Sous-section
          { wch: 30 }  // Colonne Valeur
        ];
        wsInvoice['!cols'] = wscolsInvoice;
      }
      
      // Formatage pour la feuille bookings
      if (wsBookings['!ref']) {
        const range = XLSX.utils.decode_range(wsBookings['!ref']);
        const wscolsBookings = [];
        for (let C = range.s.c; C <= range.e.c; C++) {
          let maxLength = 0;
          for (let R = range.s.r; R <= range.e.r; R++) {
            const cell = wsBookings[XLSX.utils.encode_cell({ c: C, r: R })];
            if (cell && cell.v) {
              const cellLength = cell.v.toString().length;
              if (cellLength > maxLength) maxLength = cellLength;
            }
          }
          wscolsBookings.push({ wch: Math.min(maxLength + 2, 30) });
        }
        wsBookings['!cols'] = wscolsBookings;
      }
      
      // 5. Générer le nom du fichier
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0];
      const fileName = `facture_${invoice.invoice_number}_${dateStr}.xlsx`;
      
      // 6. Télécharger le fichier
      XLSX.writeFile(wb, fileName);
      
      // 7. Fermer le modal et notifier
      toast.success(t('exportSuccess'));
      
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      toast.error(t('exportError'));
    }
  };

const exportToPDF = () => {
  if (!invoice) return;
  
  try {
    // Récupérer les réservations sélectionnées
    const selectedBookingData = invoice.bookings.filter(b => 
      selectedBookings.includes(b.booking_id)
    );
    
    if (selectedBookingData.length === 0) {
      toast.error(t('noBookingsSelected'));
      return;
    }
    
    // Calculer les totaux par currency
    const totalsByCurrency = calculateTotalsByCurrency(selectedBookingData);
    
    // Créer un nouveau document PDF
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Variables pour le positionnement
    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 5;
    
    // Couleurs - Changé bleu pour violet
    const primaryColor = [124, 58, 237]; // Violet
    const grayColor = [100, 100, 100];
    
    // ==================== 1. EN-TÊTE AVEC NUMÉRO DE FACTURE ====================
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(`INVOICE ${invoice.invoice_number}`, pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 10;
    
    // Ligne de séparation
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    
    yPosition += 15;
    
    // ==================== 2. INFORMATIONS DE L'ÉMETTEUR ====================
    const issuerY = yPosition;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor); // Titre en violet
    doc.text(t('issuer').toUpperCase(), margin, issuerY);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0); // Contenu en noir
    let currentIssuerY = issuerY + 6;
    
    // Informations de l'émetteur avec titres
    const issuerInfo = [
      { label: t('company'), value: invoice.issuer?.company || '' },
      { label: t('name'), value: invoice.issuer?.name || '' },
      { label: t('address'), value: invoice.issuer?.address || '' },
      { label: t('city'), value: `${invoice.issuer?.zipCode || ''} ${invoice.issuer?.city || ''}` },
      { label: t('country'), value: invoice.issuer?.country || '' },
      { label: t('email'), value: invoice.issuer?.email || '' },
      { label: t('phone'), value: invoice.issuer?.phone || '' }
    ].filter(info => info.value.trim() !== '');
    
    issuerInfo.forEach(info => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${info.label}:`, margin, currentIssuerY);
      
      doc.setFont('helvetica', 'normal');
        if (info.label === t('address')) {
    // Utiliser splitTextToSize de jsPDF pour diviser l'adresse
    const addressLines = doc.splitTextToSize(info.value, 40); // 40mm de largeur
    addressLines.forEach((line, lineIndex) => {
      doc.text(line, margin + 25, currentIssuerY + (lineIndex * lineHeight));
    });
    currentIssuerY += (addressLines.length * lineHeight);
  } else {
    doc.text(info.value, margin + 25, currentIssuerY);
    currentIssuerY += lineHeight;
  }
});
      
    
    // ==================== 3. INFORMATIONS DU CLIENT  ====================
    const clientX = pageWidth - margin - 80;
    const clientY = issuerY;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor); // Titre en violet
    doc.text(t('customer').toUpperCase(), clientX, clientY);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0); // Contenu en noir
    let currentClientY = clientY + 6;
    
    const customerInfo = [
      { label: t('name'), value: invoice.customer?.name || '' },
      { label: t('company'), value: invoice.customer?.company || '' },
      { label: t('address'), value: invoice.customer?.address || '' },
      { label: t('city'), value: `${invoice.customer?.zipCode || ''} ${invoice.customer?.city || ''}` },
      { label: t('country'), value: invoice.customer?.country || '' },
      { label: t('email'), value: invoice.customer?.email || '' },
      { label: t('phone'), value: invoice.customer?.phone || '' }
    ].filter(info => info.value.trim() !== '');
    
    customerInfo.forEach(info => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${info.label}:`, clientX, currentClientY);
      
      doc.setFont('helvetica', 'normal');
  if (info.label === t('address')) {
    const addressLines = doc.splitTextToSize(info.value, 40);
    addressLines.forEach((line, lineIndex) => {
      doc.text(line, clientX + 25, currentClientY + (lineIndex * lineHeight));
    });
    currentClientY += (addressLines.length * lineHeight);
  } else {
    doc.text(info.value, clientX + 25, currentClientY);
    currentClientY += lineHeight;
  }
});
    
    // Ajuster la position Y pour la suite (prendre la plus grande des deux colonnes)
    yPosition = Math.max(currentIssuerY, currentClientY) + 10;
    
    // ==================== 4. INFORMATIONS DE LA FACTURE ====================
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor); // Titre en violet
    doc.text(t('invoiceDetails').toUpperCase(), margin, yPosition);
    
    yPosition += 8;
    
    // Colonne gauche - Informations principales
    const leftColumnX = margin;
    let leftColumnY = yPosition;
    
    const leftInvoiceDetails = [
      { label: t('invoiceDate'), value: formatDate(invoice.invoice_at) },
      { label: t('dueDate'), value: formatDate(invoice.due_at) },
      { label: t('status'), value: t(invoice.status) }
    ];
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0); // Contenu en noir
    
    leftInvoiceDetails.forEach(detail => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${detail.label}:`, leftColumnX, leftColumnY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(detail.value, leftColumnX + 40, leftColumnY);
      
      leftColumnY += 6;
    });
    
    // Colonne droite - Informations financières
    const rightColumnX = pageWidth / 2 + 20;
    let rightColumnY = yPosition;
    
    const rightInvoiceDetails = [
      { label: t('totalAmount'), value: `${parseFloat(invoice.amount).toFixed(2)} ${invoice.currency}` },
      { label: t('paidDate'), value: invoice.paid_at ? formatDate(invoice.paid_at) : t('unpaid') }
    ];
    
    rightInvoiceDetails.forEach(detail => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${detail.label}:`, rightColumnX, rightColumnY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(detail.value, rightColumnX + 40, rightColumnY);
      
      rightColumnY += 6;
    });
    
    yPosition = Math.max(leftColumnY, rightColumnY) + 15;
    
    // ==================== 5. TABLEAU DES RÉSERVATIONS ====================
 const tableHeaders = [[
      t('bookingNumber'),
      t('service'),
      t('holder'),
      t('checkin'),
      t('checkout'),
      t('status'),
      t('amount')
    ]];

    const tableData = selectedBookingData.map(b => [
      b.booking_number,
      b.service.toUpperCase(),
      b.holder,
      formatDate(b.checkin),
      formatDate(b.checkout),
      b.status,
      `${parseFloat(b.amount).toFixed(2)} ${b.currency}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: tableHeaders,
      body: tableData,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: {
        fontSize: 8,
        font: 'helvetica',
        cellPadding: 3,
        overflow: 'linebreak', // FORCE LE RETOUR À LA LIGNE
        valign: 'middle'
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255], // TEXTE BLANC 
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Booking Number
        1: { cellWidth: 20 }, // Service
        2: { cellWidth: 'auto' }, // Holder (prend le reste)
        6: { halign: 'right', fontStyle: 'bold', cellWidth: 25 } // Amount
      },
      alternateRowStyles: {
        fillColor: [250, 245, 255] // Violet très clair
      }
    });

    yPosition = doc.lastAutoTable.finalY + 15;
    
    // ==================== 6. TOTAUX PAR DEVISE ====================
    // Ligne de séparation avant les totaux
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    
    yPosition += 10;
    
    // Position pour les totaux (à droite)
    const totalsStartX = pageWidth - margin - 100;
    let totalY = yPosition;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor); // Titre en violet
    doc.text(t('summary').toUpperCase(), totalsStartX, totalY);
    
    totalY += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    // Calculer le nombre total de réservations sélectionnées
    const totalBookings = selectedBookingData.length;
    
    // Ajouter une ligne pour le nombre de réservations
    doc.text(`${t('totalBookings')}:`, totalsStartX, totalY);
    doc.text(totalBookings.toString(), totalsStartX + 70, totalY, { align: 'right' });
    totalY += 6;
    
    // Ajouter une ligne vide
    totalY += 2;
    
    // Ajouter les totaux par devise
    Object.entries(totalsByCurrency).forEach(([currency, total]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('total')} ${currency}:`, totalsStartX, totalY);
      doc.text(`${total.toFixed(2)} ${currency}`, totalsStartX + 70, totalY, { align: 'right' });
      totalY += 7;
    });
    
    // ==================== 7. PIED DE PAGE ====================
    const footerY = pageHeight - 15;
    
    // Ligne de séparation
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
    
    // Informations de génération
    doc.setFontSize(8);
    doc.setTextColor(...grayColor);
    doc.setFont('helvetica', 'normal');
    
    const exportInfo = [
      `${t('exportGeneratedOn')}: ${new Date().toLocaleString(lang)}`,
      `${t('document')}: ${t('invoice')} ${invoice.invoice_number}`,
      `${t('exportType')}: PDF`
    ];
    
    let footerX = margin;
    exportInfo.forEach((info, index) => {
      doc.text(info, footerX, footerY);
      footerX += 70;
    });
    
    // ==================== 8. SAUVEGARDE ====================
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0];
    const fileName = `invoice_${invoice.invoice_number}_${dateStr}.pdf`;
    
    doc.save(fileName);
    
    // ==================== 9. NOTIFICATION ====================
    toast.success(t('exportPdfSuccess'));
    
  } catch (error) {
    console.error('Erreur lors de l\'export PDF:', error);
    toast.error(t('exportPdfError'));
  }
};

  // Styles pour le modal (inchangés)
  const modalStyles = {
    // ... (tous les styles restent identiques)
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      background: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      overflow: 'hidden'
    },
    modalHeader: {
      padding: '24px 32px',
      background: '#7c3aed',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.1)'
      }
    },
    modalContent: {
      padding: '32px',
      flex: 1,
      overflow: 'auto'
    },
    filtersSection: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap',
      alignItems: 'flex-end'
    },
    filterGroup: {
      flex: 1,
      minWidth: '200px'
    },
    filterLabel: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      '&:focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      }
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      background: 'white',
      transition: 'all 0.2s ease',
      '&:focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      }
    },
    bookingsTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '24px'
    },
    tableHeader: {
      background: '#f9fafb',
      borderBottom: '1px solid #e5e7eb'
    },
    tableHeaderCell: {
      padding: '16px',
      textAlign: 'left',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      borderBottom: '1px solid #e5e7eb'
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s ease',
      '&:hover': {
        background: '#f9fafb'
      }
    },
    tableCell: {
      padding: '16px',
      fontSize: '14px',
      color: '#374151'
    },
    checkboxCell: {
      width: '60px',
      textAlign: 'center'
    },
    checkbox: {
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      accentColor: '#7c3aed'
    },
    serviceBadge: {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'capitalize',
      display: 'inline-block'
    },
    amountCell: {
      fontWeight: '600',
      color: '#1f2937'
    },
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'capitalize',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    },
    modalFooter: {
      padding: '24px 32px',
      background: '#f9fafb',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    selectionInfo: {
      fontSize: '14px',
      color: '#6b7280'
    },
    selectedCount: {
      color: '#7c3aed',
      fontWeight: '600'
    },
    exportButton: {
      padding: '12px 32px',
      background: 'linear-gradient(135deg, #10b981 0%, #0da271 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '16px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
      },
      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
        transform: 'none'
      }
    },
    selectAllButton: {
      padding: '8px 16px',
      background: 'white',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#f9fafb',
        borderColor: '#7c3aed',
        color: '#7c3aed'
      }
    },
    // Nouveaux styles pour les boutons d'export
    exportButtonsContainer: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    pdfExportButton: {
      padding: '12px 32px',
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '16px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
      },
      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
        transform: 'none'
      }
    },
    excelExportButton: {
      padding: '12px 32px',
      background: 'linear-gradient(135deg, #10b981 0%, #0da271 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '16px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
      },
      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
        transform: 'none'
      }
    }
  };

  if (!isOpen || !invoice) return null;

  return (
    <div style={modalStyles.overlay}>
      {/* Modal de sélection pour l'export */}
      {invoice && (
        <div style={modalStyles.modal}>
          {/* En-tête du modal */}
          <div style={modalStyles.modalHeader}>
            <h2 style={modalStyles.modalTitle}>
              <Download size={24} />
              {t('exportBookingsSelection')}
            </h2>
            <button 
              style={modalStyles.closeButton}
              onClick={onClose}
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Contenu du modal */}
          <div style={modalStyles.modalContent}>
            {/* Filtres */}
            <div style={modalStyles.filtersSection}>
              <div style={modalStyles.filterGroup}>
                <label style={modalStyles.filterLabel}>
                  <Search size={16} />
                  {t('search')}
                </label>
                <div style={modalStyles.searchInput}>
                  <Search size={16} color="#9ca3af" />
                  <input
                    type="text"
                    placeholder={t('searchBookingsPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: '14px',
                      background: 'transparent'
                    }}
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#9ca3af',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
              
              <div style={modalStyles.filterGroup}>
                <label style={modalStyles.filterLabel}>
                  <Filter size={16} />
                  {t('status')}
                </label>
                <select
                  style={modalStyles.select}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">{t('allStatus')}</option>
                  <option value="confirmed">{t('confirmed')}</option>
                  <option value="cancelled">{t('cancelled')}</option>
                  <option value="pending">{t('pending')}</option>
                </select>
              </div>
              
              <div style={modalStyles.filterGroup}>
                <label style={modalStyles.filterLabel}>
                  <Filter size={16} />
                  {t('service')}
                </label>
                <select
                  style={modalStyles.select}
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                >
                  <option value="">{t('allServices')}</option>
                  <option value="hotel">{t('hotel')}</option>
                  <option value="flight">{t('flight')}</option>
                  <option value="car">{t('car')}</option>
                  <option value="activity">{t('activity')}</option>
                  <option value="package">{t('package')}</option>
                </select>
              </div>
            </div>
            
            {/* Tableau des réservations */}
            <table style={modalStyles.bookingsTable}>
              <thead style={modalStyles.tableHeader}>
                <tr>
                  <th style={modalStyles.tableHeaderCell}>
                    <input
                      type="checkbox"
                      style={modalStyles.checkbox}
                      checked={selectedBookings.length === invoice.bookings.length}
                      onChange={toggleAllBookings}
                    />
                  </th>
                  <th style={modalStyles.tableHeaderCell}>{t('bookingNumber')}</th>
                  <th style={modalStyles.tableHeaderCell}>{t('service')}</th>
                  <th style={modalStyles.tableHeaderCell}>{t('holder')}</th>
                  <th style={modalStyles.tableHeaderCell}>{t('dates')}</th>
                  <th style={modalStyles.tableHeaderCell}>{t('amount')}</th>
                  <th style={modalStyles.tableHeaderCell}>{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredBookings().map(booking => (
                  <tr key={booking.booking_id} style={modalStyles.tableRow}>
                    <td style={{...modalStyles.tableCell, ...modalStyles.checkboxCell}}>
                      <input
                        type="checkbox"
                        style={modalStyles.checkbox}
                        checked={selectedBookings.includes(booking.booking_id)}
                        onChange={() => toggleBookingSelection(booking.booking_id)}
                      />
                    </td>
                    <td style={modalStyles.tableCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getServiceIcon(booking.service)}
                        <span style={{ fontWeight: '500' }}>{booking.booking_number}</span>
                      </div>
                    </td>
                    <td style={modalStyles.tableCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ 
                          ...modalStyles.serviceBadge,
                          background: booking.service === 'hotel' ? '#dbeafe' :
                                    booking.service === 'flight' ? '#e0e7ff' :
                                    booking.service === 'car' ? '#d1fae5' :
                                    booking.service === 'activity' ? '#fef3c7' :
                                    '#f3e8ff',
                          color: booking.service === 'hotel' ? '#1e40af' :
                                booking.service === 'flight' ? '#3730a3' :
                                booking.service === 'car' ? '#047857' :
                                booking.service === 'activity' ? '#92400e' :
                                '#7c3aed'
                        }}>
                          {booking.service}
                        </span>
                        <span>{booking.service_name || booking.product_name}</span>
                      </div>
                    </td>
                    <td style={modalStyles.tableCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <User size={14} color="#6b7280" />
                        {booking.holder}
                      </div>
                    </td>
                    <td style={modalStyles.tableCell}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('checkin')}</span>
                        <span>{formatDate(booking.checkin)}</span>
                        <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{t('checkout')}</span>
                        <span>{formatDate(booking.checkout)}</span>
                      </div>
                    </td>
                    <td style={{...modalStyles.tableCell, ...modalStyles.amountCell}}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <DollarSign size={14} color="#10b981" />
                        {parseFloat(booking.amount).toFixed(2)} {booking.currency}
                      </div>
                    </td>
                    <td style={modalStyles.tableCell}>
                      <span style={{
                        ...modalStyles.statusBadge,
                        background: booking.status?.toLowerCase() === 'confirmed' ? '#d1fae5' :
                                  booking.status?.toLowerCase() === 'cancelled' ? '#fee2e2' : '#fef3c7',
                        color: booking.status?.toLowerCase() === 'confirmed' ? '#065f46' :
                              booking.status?.toLowerCase() === 'cancelled' ? '#991b1b' : '#92400e'
                      }}>
                        {booking.status?.toLowerCase() === 'confirmed' ? <CheckCircle size={12} /> :
                         booking.status?.toLowerCase() === 'cancelled' ? <XCircle size={12} /> :
                         <Clock size={12} />}
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pied du modal */}
          <div style={modalStyles.modalFooter}>
            <div style={modalStyles.selectionInfo}>
              {t('selected')}: 
              <span style={modalStyles.selectedCount}>
                {selectedBookings.length} / {invoice.bookings.length}
              </span>
              {' '}{t('bookings')}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                style={modalStyles.selectAllButton}
                onClick={toggleAllBookings}
              >
                {selectedBookings.length === invoice.bookings.length ? 
                  <Square size={16} /> : 
                  <CheckSquare size={16} />
                }
                {selectedBookings.length === invoice.bookings.length ? 
                  t('deselectAll') : 
                  t('selectAll')
                }
              </button>
              
              <div style={modalStyles.exportButtonsContainer}>
                <button
                  style={modalStyles.pdfExportButton}
                  onClick={exportToPDF}
                  disabled={selectedBookings.length === 0}
                >
                  <FileText size={18} />
                  {t('exportToPDF')}
                </button>
                
                <button
                  style={modalStyles.excelExportButton}
                  onClick={exportToExcel}
                  disabled={selectedBookings.length === 0}
                >
                  <FileSpreadsheet size={18} />
                  {t('exportToExcel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceExport;